import { Field, FieldProps, Form, Formik } from 'formik';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { TeamMemberData, UserRolesData } from 'types';
import { Button, DialogActions } from '@mui/material';
import { useQuery } from '@redux-requests/react';
import { fetchActiveUserOrganization, getRoles } from 'store/myAccount/requests';
import { GVTypography } from 'components/lib';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import { useInviteMembersMutation } from 'store/queries/organizations/useInviteMembersMutation';
import { GVLoadingBackdrop } from 'components/common';
import AddUserSummary from './AddUserSummary';
import { InviteSummary } from 'store/queries/organizations/types';
import { InviteError } from '@verify/shared-utils';
import styles from './AddUserBulk.module.css';

type AddUserBulkProps = {
  inviteSummary: InviteSummary | null;
  handleInviteSummary: (inviteSummary: InviteSummary | null) => void;
  onClose: () => void;
};

const AddUserBulk = ({ inviteSummary, handleInviteSummary, onClose }: AddUserBulkProps) => {
  const dispatch = useDispatch();
  const { mutateAsync, isPending } = useInviteMembersMutation();
  const fetchRoles = useQuery(getRoles());
  const roles: Array<UserRolesData> = fetchRoles.data || [];
  const roleUserId = roles.find((role) => role.name === 'user')?.id;
  const { data: orgData } = useQuery({ type: fetchActiveUserOrganization });
  const [initialValues, setInitialValues] = useState<TeamMemberData>({ email: '', role: roleUserId });

  const handleSubmit = async (values: TeamMemberData) => {
    const response = await mutateAsync({
      email: values.email,
      roleId: values.role || '',
      orgId: orgData?.id,
      isBulk: true,
      roleName: roles.find((role: UserRolesData) => role.id === values.role)?.name || '',
    });

    handleInviteSummary(response);
  };

  const backToEdit = () => {
    if (inviteSummary?.failedInvites.length) {
      let failedInvitesEmailString = '';
      const { failedInvites } = inviteSummary;
      const invalidEmails = failedInvites.filter((invite) => invite.error === InviteError.Invalid);

      for (let i = 0; i < invalidEmails.length; i++) {
        failedInvitesEmailString += invalidEmails[i].email;
        if (!(i === invalidEmails.length - 1)) {
          failedInvitesEmailString += ',';
        }
      }

      setInitialValues({ email: failedInvitesEmailString, role: roleUserId });
      handleInviteSummary(null);
    }
  };

  const discardInviteSummary = () => {
    if (orgData?.id) {
      dispatch(fetchActiveUserOrganization(orgData?.id, true, true));
    }

    handleInviteSummary(null);
    onClose();
  };

  return (
    <>
      {inviteSummary ? (
        <AddUserSummary inviteSummary={inviteSummary} onClose={discardInviteSummary} backToEdit={backToEdit} />
      ) : (
        <>
          {isPending && <GVLoadingBackdrop />}
          <Formik key="bulkUserForm" initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
            {(formikProps) => (
              <Form id="bulk-add-team-members-form" onSubmit={formikProps.handleSubmit} className={styles.form}>
                <div className={styles.section}>
                  <GVTypography variant="subtitle1" fontWeight={400}>
                    Add user emails separated by a comma.<br></br> Email domains must be part of your SSO connection.
                  </GVTypography>
                </div>
                <Field name="email">
                  {(fieldProps: FieldProps) => (
                    <GVTextField
                      name={fieldProps.field.name}
                      hasError={!!fieldProps.meta.error}
                      message={typeof fieldProps.meta.error === 'string' ? fieldProps.meta.error : undefined}
                      value={fieldProps.field.value}
                      onValueChange={(value) => fieldProps.form.setFieldValue(fieldProps.field.name, value ?? '')}
                      id="bulk-emails"
                      label="Emails"
                      multiline
                      rows={8}
                    />
                  )}
                </Field>
                <DialogActions className={styles.actions}>
                  <Button color="inherit" id="bulk-add-team-members-form-cancel-button" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    id="dialog-ok"
                    disabled={formikProps.values.email === ''}
                    type="submit"
                    color="secondary"
                    form="bulk-add-team-members-form"
                    variant="contained"
                  >
                    Add
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </>
      )}
    </>
  );
};

export default AddUserBulk;
